import { useState } from 'react';
import {
  AlertTriangle,
  Package,
  DollarSign,
  ShoppingCart,
  Truck,
  MessageCircle,
  RotateCcw,
  Scale,
  FileText,
  Send,
  Loader2,
  Bot,
} from 'lucide-react';
import type { ClaimClient } from './client';
import { SITE_FLAGS, stripHtml, fmtMoney, fmtDate, fmtDueIn } from './format';
import { AssistantPanel } from '../orders/AssistantPanel';

// ── Tipos (subconjunto verificado de getClaimFullDetail) ────────────────────
export interface ClaimMessage {
  sender_role: string;
  receiver_role?: string;
  message: string;
  translated_message?: string | null;
  date_created: string;
  attachments?: { filename: string; original_filename?: string }[];
}
export interface OrderContext {
  ml_order_id?: number;
  buyer_nickname?: string | null;
  paid_amount?: number | null;
  order_status?: string | null;
  item_id?: string | null;
  item_title?: string | null;
  asin?: string | null;
  amazon_cost?: number | null;
  amazon_status?: string | null;
  amazon_tracking?: string | null;
  shipment_status?: string | null;
  shipment_tracking?: string | null;
  estimated_delivery?: string | null;
}
export interface ClaimFull {
  id: number;
  status: string;
  stage: string;
  site_id: string;
  reason_id: string;
  detail: {
    due_date?: string | null;
    action_responsible?: string;
    title?: string;
    description?: string;
    problem?: string;
  } | null;
  reason_text?: string | null;
  order_context: OrderContext | null;
  profit: number | null;
  margin_pct: number | null;
  messages: ClaimMessage[];
  affects_reputation: { affects_reputation: string; due_date?: string | null } | null;
  partial_refund_offers: {
    currency_id: string;
    available_offers: { amount: number; percentage: number }[];
  } | null;
}
export interface ClaimResponse {
  success: boolean;
  data: ClaimFull;
  seller_id: number;
  product_photo: string | null;
}

const CARD = 'bg-gray-900 rounded-2xl p-4 border border-gray-800';
const LABEL = 'text-xs text-gray-500';

// ── Vista principal ─────────────────────────────────────────────────────────
export function ClaimMobileView({
  resp,
  client,
  onReload,
}: {
  resp: ClaimResponse;
  client: ClaimClient;
  onReload: () => void;
}) {
  const c = resp.data;
  const oc = c.order_context || {};
  const cur = c.partial_refund_offers?.currency_id || 'USD';

  return (
    <div className="min-h-screen bg-gray-950 text-gray-100 pb-24">
      <Header claim={c} />
      <div className="px-3 space-y-3 mt-3">
        <ProductCard claim={c} photo={resp.product_photo} />
        <MoneyRow claim={c} cur={cur} />
        <MLSale oc={oc} />
        <AmazonBuy oc={oc} />
        <ReasonCard claim={c} />
        <ChatCard claim={c} client={client} onReload={onReload} />
        <AssistantCard claim={c} sellerId={resp.seller_id} client={client} />
        <ActionsCard claim={c} client={client} onReload={onReload} cur={cur} />
      </div>
    </div>
  );
}

// ── 1. Encabezado ───────────────────────────────────────────────────────────
function Header({ claim }: { claim: ClaimFull }) {
  const due = claim.detail?.due_date;
  const overdue = due ? new Date(due).getTime() < Date.now() : false;
  return (
    <div className="bg-gray-900 px-4 pt-5 pb-4 border-b border-gray-800 sticky top-0 z-10">
      <div className="flex items-center gap-2 text-sm text-gray-400">
        <span className="text-xl">{SITE_FLAGS[claim.site_id] || '🌐'}</span>
        <span>Reclamo #{claim.id}</span>
      </div>
      <div className="flex flex-wrap gap-2 mt-2">
        <Badge tone={claim.status === 'opened' ? 'yellow' : 'gray'}>
          {claim.status === 'opened' ? 'Abierto' : 'Cerrado'}
        </Badge>
        <Badge tone={claim.stage === 'dispute' ? 'red' : 'blue'}>
          {claim.stage === 'dispute' ? 'En mediación' : 'Reclamo'}
        </Badge>
        {claim.affects_reputation?.affects_reputation === 'affected' && (
          <Badge tone="red">Afecta reputación</Badge>
        )}
      </div>
      {due && (
        <div className={`mt-2 text-sm font-medium ${overdue ? 'text-red-400' : 'text-amber-400'}`}>
          ⏰ {fmtDueIn(due)} ({fmtDate(due)})
        </div>
      )}
    </div>
  );
}

// ── 2. Producto ─────────────────────────────────────────────────────────────
function ProductCard({ claim, photo }: { claim: ClaimFull; photo: string | null }) {
  const oc = claim.order_context || {};
  return (
    <div className={CARD}>
      <SectionTitle icon={<Package className="w-4 h-4" />}>Producto</SectionTitle>
      <div className="flex gap-3 mt-2">
        {photo ? (
          <img
            src={photo}
            alt=""
            className="w-24 h-24 rounded-xl object-cover bg-gray-800 flex-shrink-0"
            loading="lazy"
          />
        ) : (
          <div className="w-24 h-24 rounded-xl bg-gray-800 flex items-center justify-center flex-shrink-0">
            <Package className="w-8 h-8 text-gray-600" />
          </div>
        )}
        <div className="min-w-0">
          <div className="text-sm font-medium leading-snug">{oc.item_title || '—'}</div>
          {oc.asin && <div className="text-xs text-gray-500 mt-1">Amazon: {oc.asin}</div>}
          {oc.item_id && <div className="text-xs text-gray-500">ML: {oc.item_id}</div>}
        </div>
      </div>
    </div>
  );
}

// ── 3. Plata de un vistazo ──────────────────────────────────────────────────
function MoneyRow({ claim, cur }: { claim: ClaimFull; cur: string }) {
  const oc = claim.order_context || {};
  return (
    <div className={CARD}>
      <SectionTitle icon={<DollarSign className="w-4 h-4" />}>Resumen de plata</SectionTitle>
      <div className="grid grid-cols-3 gap-2 mt-2 text-center">
        <Stat label="Venta ML" value={fmtMoney(oc.paid_amount, cur)} />
        <Stat label="Costo Amazon" value={fmtMoney(oc.amazon_cost, cur)} />
        <Stat
          label="Ganancia"
          value={fmtMoney(claim.profit, cur)}
          sub={claim.margin_pct != null ? `${Math.round(claim.margin_pct)}%` : undefined}
          tone={claim.profit != null && claim.profit >= 0 ? 'green' : 'red'}
        />
      </div>
    </div>
  );
}

// ── 4. Venta ML ─────────────────────────────────────────────────────────────
function MLSale({ oc }: { oc: OrderContext }) {
  return (
    <div className={CARD}>
      <SectionTitle icon={<ShoppingCart className="w-4 h-4" />}>Venta en Mercado Libre</SectionTitle>
      <Field label="Comprador" value={oc.buyer_nickname || '—'} />
      <Field label="Orden" value={oc.ml_order_id ? `#${oc.ml_order_id}` : '—'} />
      <Field label="Estado del envío" value={oc.shipment_status || '—'} />
      <Field label="Seguimiento" value={oc.shipment_tracking || '—'} mono />
      <Field label="Entrega estimada" value={fmtDate(oc.estimated_delivery)} />
    </div>
  );
}

// ── 5. Compra Amazon ────────────────────────────────────────────────────────
function AmazonBuy({ oc }: { oc: OrderContext }) {
  return (
    <div className={CARD}>
      <SectionTitle icon={<Truck className="w-4 h-4" />}>Compra en Amazon</SectionTitle>
      <Field label="Estado" value={oc.amazon_status || '—'} />
      <Field label="Seguimiento" value={oc.amazon_tracking || '—'} mono />
    </div>
  );
}

// ── 6. Motivo ───────────────────────────────────────────────────────────────
function ReasonCard({ claim }: { claim: ClaimFull }) {
  const d = claim.detail || {};
  // Fallback cuando ML no manda detail (típico cancel_purchase): texto del
  // catálogo de motivos; último recurso, el código crudo.
  const fallback = !d.title && !d.problem ? claim.reason_text || claim.reason_id : null;
  if (!d.title && !d.problem && !d.description && !fallback) return null;
  return (
    <div className={CARD}>
      <SectionTitle icon={<AlertTriangle className="w-4 h-4" />}>Motivo del reclamo</SectionTitle>
      {d.title && <div className="text-sm font-medium mt-1">{stripHtml(d.title)}</div>}
      {d.problem && <div className="text-sm text-gray-300 mt-1">{stripHtml(d.problem)}</div>}
      {fallback && <div className="text-sm text-gray-300 mt-1">{fallback}</div>}
      {d.description && <div className="text-xs text-gray-500 mt-1">{stripHtml(d.description)}</div>}
    </div>
  );
}

// ── 7. Chat ─────────────────────────────────────────────────────────────────
function ChatCard({
  claim,
  client,
  onReload,
}: {
  claim: ClaimFull;
  client: ClaimClient;
  onReload: () => void;
}) {
  const [text, setText] = useState('');
  const [to, setTo] = useState<'complainant' | 'mediator'>(
    claim.stage === 'dispute' ? 'mediator' : 'complainant'
  );
  const [sending, setSending] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const send = async () => {
    if (text.trim().length < 2) return;
    setSending(true);
    setErr(null);
    try {
      await client.post('/claim/message', { message: text.trim(), receiver_role: to });
      setText('');
      onReload();
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'No se pudo enviar');
    } finally {
      setSending(false);
    }
  };

  const msgs = [...(claim.messages || [])].sort(
    (a, b) => new Date(a.date_created).getTime() - new Date(b.date_created).getTime()
  );

  return (
    <div className={CARD}>
      <SectionTitle icon={<MessageCircle className="w-4 h-4" />}>Conversación</SectionTitle>
      <div className="space-y-2 mt-2 max-h-96 overflow-y-auto">
        {msgs.length === 0 && <div className="text-sm text-gray-500">Sin mensajes todavía.</div>}
        {msgs.map((m, i) => {
          const mine = m.sender_role === 'respondent';
          const body = stripHtml(m.translated_message || m.message);
          return (
            <div key={i} className={`flex ${mine ? 'justify-end' : 'justify-start'}`}>
              <div
                className={`max-w-[80%] rounded-2xl px-3 py-2 text-sm ${
                  mine ? 'bg-blue-600 text-white' : 'bg-gray-800 text-gray-100'
                }`}
              >
                <div className="text-[10px] opacity-70 mb-0.5">{roleLabel(m.sender_role)}</div>
                <div className="whitespace-pre-wrap break-words">{body}</div>
                {m.attachments?.map((a, j) => (
                  <Attachment key={j} att={a} client={client} />
                ))}
                <div className="text-[10px] opacity-60 mt-1">{fmtDate(m.date_created)}</div>
              </div>
            </div>
          );
        })}
      </div>

      <div className="mt-3 flex gap-1 text-xs">
        <TabBtn active={to === 'complainant'} onClick={() => setTo('complainant')}>
          Al cliente
        </TabBtn>
        <TabBtn active={to === 'mediator'} onClick={() => setTo('mediator')}>
          A la mediación
        </TabBtn>
      </div>
      <div className="mt-2 flex gap-2">
        <textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder="Escribí tu respuesta…"
          rows={2}
          className="flex-1 bg-gray-800 rounded-xl px-3 py-2 text-sm resize-none outline-none"
        />
        <button
          onClick={send}
          disabled={sending || text.trim().length < 2}
          className="px-3 rounded-xl bg-blue-600 text-white disabled:opacity-40 flex items-center"
        >
          {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
        </button>
      </div>
      {err && <div className="text-xs text-red-400 mt-1">{err}</div>}
    </div>
  );
}

// Adjunto del chat: imágenes se muestran inline (tap abre el original en otra
// pestaña); el resto queda como enlace de descarga. El <img> no puede mandar
// headers → el token firmado viaja por query ?t= (igual que el SSE del chat).
const IMG_RE = /\.(jpe?g|png|gif|webp|bmp|heic)$/i;
function Attachment({
  att,
  client,
}: {
  att: { filename: string; original_filename?: string };
  client: ClaimClient;
}) {
  const url = `/api/claim-app/claim/attachment/${encodeURIComponent(att.filename)}?t=${encodeURIComponent(client.token)}`;
  const name = att.original_filename || att.filename;
  if (IMG_RE.test(att.filename) || IMG_RE.test(name)) {
    return (
      <a href={url} target="_blank" rel="noreferrer" className="block mt-1">
        <img
          src={url}
          alt={name}
          loading="lazy"
          className="rounded-lg max-h-48 max-w-full object-contain bg-gray-900/40"
        />
      </a>
    );
  }
  return (
    <a href={url} target="_blank" rel="noreferrer" className="block text-[11px] underline mt-1 opacity-80">
      📎 {name}
    </a>
  );
}

// ── 8. Asistente IA ─────────────────────────────────────────────────────────
function AssistantCard({
  claim,
  sellerId,
  client,
}: {
  claim: ClaimFull;
  sellerId: number;
  client: ClaimClient;
}) {
  const [open, setOpen] = useState(false);
  return (
    <div className={CARD}>
      <button className="w-full flex items-center justify-between" onClick={() => setOpen((v) => !v)}>
        <SectionTitle icon={<Bot className="w-4 h-4" />}>Asistente de IA</SectionTitle>
        <span className="text-xs text-gray-400">{open ? 'Ocultar' : 'Abrir'}</span>
      </button>
      {open && (
        <div className="mt-2">
          <AssistantPanel
            mlClaimId={claim.id}
            sellerId={sellerId}
            baseUrl="/api/claim-app/assistant"
            token={client.token}
          />
        </div>
      )}
    </div>
  );
}

// ── 9. Acciones ─────────────────────────────────────────────────────────────
function ActionsCard({
  claim,
  client,
  onReload,
  cur,
}: {
  claim: ClaimFull;
  client: ClaimClient;
  onReload: () => void;
  cur: string;
}) {
  const offers = claim.partial_refund_offers?.available_offers || [];
  return (
    <div className={CARD}>
      <SectionTitle icon={<Scale className="w-4 h-4" />}>Acciones</SectionTitle>
      <div className="space-y-2 mt-2">
        <ConfirmAction
          label="Aceptar devolución"
          icon={<RotateCcw className="w-4 h-4" />}
          confirmText="¿Aceptar la devolución de este reclamo?"
          run={() => client.post('/claim/return')}
          onDone={onReload}
        />
        <ConfirmAction
          label="Reembolso total"
          icon={<DollarSign className="w-4 h-4" />}
          tone="red"
          confirmText="¿Devolver el 100% del dinero al cliente? Esta acción mueve plata."
          run={() => client.post('/claim/refund')}
          onDone={onReload}
        />
        {offers.length > 0 && (
          <PartialRefund offers={offers} cur={cur} client={client} onDone={onReload} />
        )}
        <ConfirmAction
          label="Escalar a mediación de ML"
          icon={<Scale className="w-4 h-4" />}
          confirmText="¿Pedir que Mercado Libre intervenga como mediador?"
          run={() => client.post('/claim/dispute')}
          onDone={onReload}
        />
        <ConfirmAction
          label="Enviar factura"
          icon={<FileText className="w-4 h-4" />}
          confirmText="¿Enviar la factura al cliente por este reclamo?"
          run={() => client.post('/claim/send-invoice')}
          onDone={onReload}
        />
      </div>
    </div>
  );
}

function PartialRefund({
  offers,
  cur,
  client,
  onDone,
}: {
  offers: { amount: number; percentage: number }[];
  cur: string;
  client: ClaimClient;
  onDone: () => void;
}) {
  const [pct, setPct] = useState<number>(offers[0]?.percentage ?? 50);
  const chosen = offers.find((o) => o.percentage === pct);
  return (
    <div className="bg-gray-800/60 rounded-xl p-3">
      <div className="text-sm font-medium mb-2">Reembolso parcial</div>
      <div className="flex flex-wrap gap-1 mb-2">
        {offers.map((o) => (
          <button
            key={o.percentage}
            onClick={() => setPct(o.percentage)}
            className={`px-2 py-1 rounded-lg text-xs ${
              pct === o.percentage ? 'bg-blue-600 text-white' : 'bg-gray-700 text-gray-200'
            }`}
          >
            {o.percentage}%
          </button>
        ))}
      </div>
      <ConfirmAction
        label={`Devolver ${pct}% (${fmtMoney(chosen?.amount, cur)})`}
        icon={<DollarSign className="w-4 h-4" />}
        tone="red"
        confirmText={`¿Devolver ${pct}% (${fmtMoney(chosen?.amount, cur)}) al cliente?`}
        run={() => client.post('/claim/partial-refund', { percentage: pct })}
        onDone={onDone}
      />
    </div>
  );
}

// ── Primitivos ──────────────────────────────────────────────────────────────
function ConfirmAction({
  label,
  icon,
  confirmText,
  run,
  onDone,
  tone = 'gray',
}: {
  label: string;
  icon: React.ReactNode;
  confirmText: string;
  run: () => Promise<unknown>;
  onDone: () => void;
  tone?: 'gray' | 'red';
}) {
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const exec = async () => {
    setBusy(true);
    setErr(null);
    try {
      await run();
      setConfirming(false);
      onDone();
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Error');
    } finally {
      setBusy(false);
    }
  };

  if (confirming) {
    return (
      <div className="bg-gray-800 rounded-xl p-3">
        <div className="text-sm mb-2">{confirmText}</div>
        {err && <div className="text-xs text-red-400 mb-2">{err}</div>}
        <div className="flex gap-2">
          <button
            onClick={exec}
            disabled={busy}
            className="flex-1 py-2 rounded-lg bg-red-600 text-white text-sm disabled:opacity-40 flex items-center justify-center gap-1"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Sí, confirmar'}
          </button>
          <button
            onClick={() => setConfirming(false)}
            disabled={busy}
            className="flex-1 py-2 rounded-lg bg-gray-700 text-gray-200 text-sm"
          >
            Cancelar
          </button>
        </div>
      </div>
    );
  }

  return (
    <button
      onClick={() => setConfirming(true)}
      className={`w-full py-2.5 rounded-xl text-sm font-medium flex items-center justify-center gap-2 ${
        tone === 'red' ? 'bg-red-600/20 text-red-300 border border-red-600/40' : 'bg-gray-800 text-gray-100'
      }`}
    >
      {icon}
      {label}
    </button>
  );
}

function SectionTitle({ icon, children }: { icon: React.ReactNode; children: React.ReactNode }) {
  return (
    <div className="flex items-center gap-2 text-sm font-semibold text-gray-300">
      {icon}
      {children}
    </div>
  );
}

function Field({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div className="flex justify-between gap-3 py-1 border-b border-gray-800/60 last:border-0">
      <span className={LABEL}>{label}</span>
      <span className={`text-sm text-right ${mono ? 'font-mono text-xs' : ''} break-all`}>
        {value}
      </span>
    </div>
  );
}

function Stat({
  label,
  value,
  sub,
  tone,
}: {
  label: string;
  value: string;
  sub?: string;
  tone?: 'green' | 'red';
}) {
  const color = tone === 'green' ? 'text-green-400' : tone === 'red' ? 'text-red-400' : 'text-gray-100';
  return (
    <div>
      <div className={LABEL}>{label}</div>
      <div className={`text-sm font-semibold ${color}`}>{value}</div>
      {sub && <div className="text-[11px] text-gray-500">{sub}</div>}
    </div>
  );
}

function Badge({ children, tone }: { children: React.ReactNode; tone: 'yellow' | 'red' | 'blue' | 'gray' }) {
  const map = {
    yellow: 'bg-yellow-500/15 text-yellow-300 border-yellow-500/30',
    red: 'bg-red-500/15 text-red-300 border-red-500/30',
    blue: 'bg-blue-500/15 text-blue-300 border-blue-500/30',
    gray: 'bg-gray-500/15 text-gray-300 border-gray-500/30',
  };
  return <span className={`text-xs px-2 py-0.5 rounded-full border ${map[tone]}`}>{children}</span>;
}

function TabBtn({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      onClick={onClick}
      className={`px-3 py-1 rounded-lg ${active ? 'bg-blue-600 text-white' : 'bg-gray-800 text-gray-300'}`}
    >
      {children}
    </button>
  );
}

function roleLabel(role: string): string {
  if (role === 'respondent') return 'Vos';
  if (role === 'complainant') return 'Cliente';
  if (role === 'mediator') return 'Mediación ML';
  return role;
}
